import { apiFetch } from '@/lib/api/http';
import type { Role } from '@/features/auth/api';

export type ThreadKind = 'direct' | 'event_group';

// 「個別」だと会員同士でやり取りできるように読めるが、実際は事務局宛て固定なので
// 相手が分かる名前にする（D-058）
export const THREAD_KIND_LABELS: Record<ThreadKind, string> = {
  direct: '事務局への問い合わせ',
  event_group: 'グループメッセージ',
};

/** 画面上で「このスレッドは何か」を1行で説明する（D-056） */
export const THREAD_KIND_DESCRIPTIONS: Record<ThreadKind, string> = {
  direct:
    '事務局への問い合わせです。宛先は事務局に固定されていて、受講生・理事・講師・サクセス担当の個人を相手に選ぶことはできません。事務局の担当者が返信します。',
  event_group:
    'イベントごとに作られるグループです。その回の参加者と運営が、当日までの連絡に使います。サクセス面談・1on1もイベントとして作られるため、その連絡もここで行います（参加者が2人でも同じ扱いです）。',
};

export type InboxStatus = 'open' | 'in_progress' | 'waiting' | 'resolved';
export type InboxPriority = 'low' | 'normal' | 'high' | 'urgent';

export const INBOX_STATUS_LABELS: Record<InboxStatus, string> = {
  open: '未対応',
  in_progress: '対応中',
  waiting: '保留',
  resolved: '解決済み',
};

export const INBOX_PRIORITY_LABELS: Record<InboxPriority, string> = {
  low: '低',
  normal: '中',
  high: '高',
  urgent: '緊急',
};

/** 同じ絵文字を押した人のまとまり（D-317） */
export interface MessageReactionGroup {
  emoji: string;
  count: number;
  /** 押した人の名前（ホバーで出す） */
  names: string[];
  /** 自分が押していれば、もう一度押すと取り消しになる */
  reacted_by_me: boolean;
}

/** 画面から押せる絵文字（バックエンドの ALLOWED_REACTIONS と揃える / D-317） */
export const REACTION_CHOICES = ['👍', '🙏', '✅', '😊', '🎉', '👀'] as const;

export interface MessageItem {
  id: string;
  thread_id: string;
  sender_user_id: string | null;
  sender_name: string | null;
  sender_role: Role | null;
  /** アイコン表示・受講生詳細へのリンク用（D-167） */
  sender_photo_url: string | null;
  sender_member_id: string | null;
  body: string;
  /** 閲覧用の短命URL（保存キーではない） */
  attachment_url: string | null;
  attachment_name: string | null;
  attachment_size: number | null;
  is_deleted: boolean;
  created_at: string;
  reactions: MessageReactionGroup[];
  /** 本文で名指しされた人。自分が入っていれば画面で強調する（D-318） */
  mentioned_user_ids: string[];
}

/** 添付アップロードの結果。この key をそのまま投稿に載せる（D-145） */
export interface MessageAttachment {
  key: string;
  filename: string;
  size: number;
  content_type: string;
}

/** 相手がどこまで読んだか（FBメッセンジャーと同じ既読表示 / D-145） */
export interface ThreadReadReceipt {
  user_id: string | null;
  name: string;
  photo_url: string | null;
  read_at: string;
}

/** スレッドの参加者（自分以外） */
export interface ThreadParticipant {
  user_id: string;
  name: string | null;
  role: Role | null;
  /** アイコン表示・受講生詳細へのリンク用（D-167） */
  photo_url: string | null;
  member_id: string | null;
}

export interface ThreadSummary {
  id: string;
  kind: ThreadKind;
  event_id: string | null;
  event_title: string | null;
  is_closed: boolean;
  retention_until: string | null;
  last_message_at: string | null;
  last_message_preview: string | null;
  unread: boolean;
  updated_at: string;
  participants: ThreadParticipant[];
}

export interface ThreadDetail {
  id: string;
  kind: ThreadKind;
  event_id: string | null;
  event_title: string | null;
  is_closed: boolean;
  retention_until: string | null;
  can_post: boolean;
  messages: MessageItem[];
  /** direct は自分以外。event_group は自分を含む全員（D-235） */
  participants: ThreadParticipant[];
  read_receipts: ThreadReadReceipt[];
  created_at: string;
  updated_at: string;
}

export interface ThreadCreateInput {
  kind: ThreadKind;
  body: string;
  /** 事務局から個人へ送るときの相手（D-134）。相手1人につき会話は1本に集約される（D-138） */
  recipient_user_id?: string;
}

/** 事務局の連絡先一覧の1件（会員・理事講師・サクセス担当 / D-138） */
export interface ContactSummary {
  user_id: string;
  name: string;
  role: Role | null;
  /** アイコン表示・受講生詳細へのリンク用（D-167） */
  photo_url: string | null;
  member_id: string | null;
  thread_id: string | null;
  last_message_at: string | null;
  last_message_preview: string | null;
  unread: boolean;
}

/**
 * 会話の見出し（D-151）。件名は持たないので、「誰と／どの回の」やり取りかを出す。
 * - グループメッセージ: イベント名
 * - 事務局↔個人: 相手の名前（受講生から見れば常に「事務局」）
 */
export function threadHeading(
  thread: Pick<ThreadSummary, 'kind' | 'event_title' | 'participants'>,
): string {
  if (thread.event_title) return thread.event_title;
  const names = (thread.participants ?? []).map((p) => p.name ?? '（不明）').filter(Boolean);
  if (names.length > 0) return names.join('、');
  return thread.kind === 'direct' ? '事務局' : THREAD_KIND_LABELS[thread.kind];
}

export function fetchContacts(query: PeopleQuery = {}): Promise<ContactSummary[]> {
  const params = peopleQueryParams(query);
  const search = params.toString();
  return apiFetch<ContactSummary[]>(`/threads/contacts${search ? `?${search}` : ''}`);
}

export function fetchThreads(
  kind?: ThreadKind | null,
  participantUserId?: string,
): Promise<ThreadSummary[]> {
  const params = new URLSearchParams();
  if (kind) params.set('kind', kind);
  if (participantUserId) params.set('participant_user_id', participantUserId);
  const query = params.toString();
  return apiFetch<ThreadSummary[]>(`/threads${query ? `?${query}` : ''}`);
}

export function fetchThread(threadId: string): Promise<ThreadDetail> {
  return apiFetch<ThreadDetail>(`/threads/${threadId}`);
}

export function createThread(payload: ThreadCreateInput): Promise<ThreadSummary> {
  return apiFetch<ThreadSummary>('/threads', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function postMessage(
  threadId: string,
  body: string,
  attachment?: MessageAttachment | null,
): Promise<MessageItem> {
  return apiFetch<MessageItem>(`/threads/${threadId}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      body,
      attachment_url: attachment?.key ?? null,
      attachment_name: attachment?.filename ?? null,
      attachment_size: attachment?.size ?? null,
    }),
  });
}

/** 添付を先に保存する。返ってきた結果を postMessage に渡す（D-145） */
export function uploadMessageAttachment(
  threadId: string,
  file: File,
): Promise<MessageAttachment> {
  const form = new FormData();
  form.append('file', file);
  return apiFetch<MessageAttachment>(`/threads/${threadId}/attachments`, {
    method: 'POST',
    body: form,
  });
}

export function markThreadRead(threadId: string): Promise<void> {
  return apiFetch<void>(`/threads/${threadId}/read`, { method: 'POST' });
}

/** リアクションの追加・取り消し（同じ絵文字を2回送るとトグル / D-317）。更新後のメッセージを返す */
export function toggleMessageReaction(messageId: string, emoji: string): Promise<MessageItem> {
  return apiFetch<MessageItem>(`/messages/${messageId}/reactions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ emoji }),
  });
}

export function deleteMessage(messageId: string): Promise<void> {
  return apiFetch<void>(`/messages/${messageId}`, { method: 'DELETE' });
}

export function fetchThreadsUnreadCount(): Promise<{ unread: number }> {
  return apiFetch<{ unread: number }>('/threads/unread-count');
}

// --- 共有受信箱（事務局専用） --------------------------------------------------

export interface InboxItemSummary {
  id: string;
  thread_id: string;
  requester_name: string | null;
  /** アイコン表示・受講生詳細へのリンク用（D-167） */
  requester_photo_url: string | null;
  requester_member_id: string | null;
  requester_role?: Role | null;
  assignee_user_id: string | null;
  assignee_name: string | null;
  status: InboxStatus;
  priority: InboxPriority;
  sla_due_at: string | null;
  sla_overdue: boolean;
  last_message_at: string | null;
  created_at: string;
}

export interface InboxItemDetail extends InboxItemSummary {
  internal_memo: string | null;
}

export interface InboxQuery {
  status?: InboxStatus | null;
  priority?: InboxPriority | null;
  assignee_user_id?: string | null;
  role?: Role | null;
  cohort_id?: string | null;
  course_id?: string | null;
  q?: string | null;
}

export interface PeopleQuery {
  role?: Role | null;
  cohort_id?: string | null;
  course_id?: string | null;
  q?: string | null;
}

function peopleQueryParams(query: PeopleQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.role) params.set('role', query.role);
  if (query.cohort_id) params.set('cohort_id', query.cohort_id);
  if (query.course_id) params.set('course_id', query.course_id);
  if (query.q) params.set('q', query.q);
  return params;
}

export function fetchInbox(query: InboxQuery = {}): Promise<InboxItemSummary[]> {
  const params = peopleQueryParams(query);
  if (query.status) params.set('status', query.status);
  if (query.priority) params.set('priority', query.priority);
  if (query.assignee_user_id) params.set('assignee_user_id', query.assignee_user_id);
  const search = params.toString();
  return apiFetch<InboxItemSummary[]>(`/inbox${search ? `?${search}` : ''}`);
}

export function fetchInboxItem(inboxItemId: string): Promise<InboxItemDetail> {
  return apiFetch<InboxItemDetail>(`/inbox/${inboxItemId}`);
}

export interface InboxItemUpdateInput {
  assignee_user_id?: string | null;
  status?: InboxStatus;
  priority?: InboxPriority;
  sla_due_at?: string | null;
  internal_memo?: string | null;
}

export function updateInboxItem(
  inboxItemId: string,
  payload: InboxItemUpdateInput,
): Promise<InboxItemDetail> {
  return apiFetch<InboxItemDetail>(`/inbox/${inboxItemId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}
